import { useMemo } from 'react';

import { marginService } from '@swyftx/margin';
import { MarginOrderType, MarginPositionStatus } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';

import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { useMarginPosition } from './useMarginPosition';
import { getMarketPrecisionScale, getPositionOrderDirection } from '../../utils';
import { useMarginAccount } from '../account';
import { useMarginInstruments } from '../instruments';
import { useMarginPrices } from '../prices';

type Props = {
  positionId?: string;
};

const useMarginPositionDetails = ({ positionId }: Props) => {
  const { getPositionById } = useMarginPosition();
  const { getMarketByPosition } = useMarginInstruments();
  const { getAssetByCode, getAssetByUuid } = useMarkets();
  const { getCurrencyRate, getMarketPriceByInstrument } = useMarginPrices();
  const { commission: accountCommission } = useMarginAccount();

  const position = useMemo(() => getPositionById(positionId), [getPositionById, positionId]);

  const positionMarket = getMarketByPosition(position);
  const positionMarketLotsPrecision = getMarketPrecisionScale(positionMarket?.minLotIncrement) || 0;

  const usedMargin = position ? Big(position.marginUsed || '0') : undefined;

  const profitLoss = position ? Big(position.profit || '0') : undefined;

  const stopLoss = position ? Big(position.stopLoss || '0') : undefined;
  const takeProfit = position ? Big(position.takeProfit || '0') : undefined;

  const side = position ? getPositionOrderDirection(position) : undefined;

  const lots = !position?.filledOpen
    ? Big(position?.volume)
    : Big(position?.filledOpen).minus(position?.filledClose || 0);

  const price = Big(position?.openPrice || position?.limitPrice || position?.stopPrice || '0').toFixed(
    positionMarket?.precision,
  );

  const commission = useMemo(() => {
    if (!position) return undefined;

    if (position.status === MarginPositionStatus.Pending) {
      return marginService.calculateCommission({
        lots: lots.toNumber(),
        orderPrice: price,
        commissionPercentage: accountCommission.times(100).toNumber(),
      });
    }

    return Big(position.commission || '0');
  }, [accountCommission, lots, position, price]);

  const currencyPrice = useMemo(
    () => Big(position?.openPrice || position?.limitPrice || position?.stopPrice || 0).times(getCurrencyRate()),
    [getCurrencyRate, position?.limitPrice, position?.openPrice, position?.stopPrice],
  );

  const currentPrice = useMemo(() => {
    if (!position) return undefined;

    if (position.currentPrice) return Big(position.currentPrice).toFixed(positionMarket?.precision);

    const currencyAsset = getAssetByCode(position?.currency);
    const secondaryAsset = getAssetByUuid(positionMarket?.secondaryAssetUuid);

    if (!currencyAsset || !secondaryAsset || !positionMarket) return undefined;

    const marketPrice = getMarketPriceByInstrument(positionMarket);

    return Big(position.sell ? marketPrice?.bid : marketPrice?.ask).toFixed(positionMarket.precision);
  }, [getAssetByCode, getAssetByUuid, getMarketPriceByInstrument, position, positionMarket]);

  const currentAudPrice = useMemo(() => {
    if (!currentPrice || !position) return undefined;

    return Big(currentPrice).times(getCurrencyRate(position.sell ? 'bid' : 'ask'));
  }, [currentPrice, getCurrencyRate, position]);

  const type = useMemo(() => {
    if (!position) {
      return MarginOrderType.Market;
    } else if (position.limitPrice) {
      return MarginOrderType.Limit;
    } else if (position.stopPrice) {
      return MarginOrderType.Stop;
    }

    return MarginOrderType.Market;
  }, [position]);

  const priceScale = positionMarket?.precision;

  const isPending = !position ? false : [MarginPositionStatus.Pending].includes(position.status);

  const isOpen = !position ? false : [MarginPositionStatus.Open].includes(position.status);

  const isClosed = !position
    ? false
    : [MarginPositionStatus.Cancelled, MarginPositionStatus.Closed, MarginPositionStatus.Reject].includes(
        position.status,
      );

  const secondaryAsset = getAssetByUuid(positionMarket?.secondaryAssetUuid);

  const primaryAsset = getAssetByUuid(positionMarket?.primaryAssetUuid);

  const isLimitOrder = !!position?.limitPrice;
  const isStopOrder = !!position?.stopPrice;
  const isShort = position?.sell;
  const isLong = !position?.sell;

  const canPartialClose = useMemo(() => {
    if (!positionMarket?.close) return false;

    if (Big(positionMarket?.minLots).eq(lots)) return false;
    if (Big(positionMarket?.minLots).times(2).gt(lots)) return false;

    return true;
  }, [lots, positionMarket?.close, positionMarket?.minLots]);

  const tradeType = useMemo((): MarginOrderType => {
    if (position?.limitPrice) return MarginOrderType.Limit;
    if (position?.stopPrice) return MarginOrderType.Stop;

    return MarginOrderType.Market;
  }, [position?.limitPrice, position?.stopPrice]);

  return {
    position,
    positionMarket,
    positionMarketLotsPrecision,
    usedMargin,
    profitLoss,
    commission,
    side,
    lots,
    type,
    price,
    currencyPrice,
    currentPrice,
    currentAudPrice,
    priceScale,
    isPending,
    isOpen,
    isClosed,
    isLimitOrder,
    isStopOrder,
    isShort,
    isLong,
    secondaryAsset,
    primaryAsset,
    canPartialClose,
    tradeType,
    stopLoss,
    takeProfit,
  };
};

export { useMarginPositionDetails };
